import Connector, { type Attachable } from '../objects/Connector';
import type Canvas from '../objects/Canvas';
import { AttachmentPoint, ConnectorEnd } from '../types';
import type Shape from '../objects/Shape';
import type ShapeConnectControl from '../controls/ShapeConnectControl';
import { type StylusConnectorOptions } from './StylusConnector';
import { type IPoint } from 'fabric/fabric-impl';
import { type MouseEventData } from '../external-types';
import DraggableBrush from './DraggableBrush';
import { type fabric } from 'fabric';
import { calculateAttachmentPosition } from '../utils/connectorAttachment';
import {
  buildShape,
  buildShapeAndConnector,
} from '../services/duplication/duplicatingObjects';

const CLICK_TIMEOUT = 200;
const CLICK_RADIUS = 10;
const DEFAULT_LENGTH = 75;

interface ClickStart {
  started: number;
  pointer: IPoint;
}

// Brush for drawing connectors.
class ConnectorDrawingBrush extends DraggableBrush {
  private connector: Connector | null = null;
  private clickStart: ClickStart | undefined;

  constructor(
    private readonly canvas: Canvas,
    private readonly initialOptions?: StylusConnectorOptions,
    private readonly shapeControl?: ShapeConnectControl,
    private readonly originalObject?: Shape,
    private readonly drawingComplete?: () => void
  ) {
    super();
  }

  objectInProgress() {
    return this.connector;
  }

  async startDragging(pointer: IPoint) {
    const controller = this.canvas.controller;
    const endPoint = { x: pointer.x + DEFAULT_LENGTH, y: pointer.y };
    const { iconName, ...options } = this.initialOptions ?? {};
    this.connector = Connector.create([pointer, endPoint], {
      controller,
      ...controller.defaultsManager.for('connector'),
      ...options,
    });
    this.connector.beingControlled = true;
    if (iconName) {
      this.connector.createIconLabel({
        iconName,
      });
    }
    this.canvas.setActiveObject(this.connector);
    return this.objectInProgress();
  }

  onMouseDown(pointer: IPoint, eventData: MouseEventData) {
    if (!this.canvas._isMainEvent(eventData.e)) {
      return;
    }

    this.connector = null;
    this.canvas.controller.actionSetStylusMenu(null);

    const attachmentPosition = this.shapeControl
      ? calculateAttachmentPosition(
          this.shapeControl.currentObject,
          this.shapeControl.attachmentPoint,
          { withStandoff: true }
        )
      : undefined;
    this.clickStart = {
      started: Date.now(),
      pointer: attachmentPosition ?? pointer,
    };
  }

  onMouseMove(pointer: IPoint, eventData: MouseEventData) {
    if (!this.canvas._isMainEvent(eventData.e)) {
      return;
    }

    if (!this.connector && this.clickStart) {
      // See if we should start making a dynamic connector.
      if (
        Date.now() > this.clickStart.started + CLICK_TIMEOUT &&
        (Math.abs(pointer.x - this.clickStart.pointer.x) > CLICK_RADIUS ||
          Math.abs(pointer.y - this.clickStart.pointer.y) > CLICK_RADIUS)
      ) {
        this._createConnector({
          eventData,
          drawing: true,
          clickStart: this.clickStart,
        });
      }
    } else {
      this.canvas._transformObject(eventData.e);
    }
  }

  onMouseUp(eventData: MouseEventData) {
    if (!this.canvas._isMainEvent(eventData.e)) {
      return true;
    }

    if (!this.connector && this.clickStart) {
      // The user just clicked without dragging. Create a connector and shape.
      if (this.shapeControl) {
        this.handleClick(this.shapeControl);
      } else {
        const connector = this._createConnector({
          eventData,
          drawing: false,
          clickStart: this.clickStart,
        });
        connector.beingControlled = false;
      }
    } else {
      // The user finished a drag.
      // Note: we don't ever seem to evaluate this block, as it seems "drag end" is now
      // handled elsewhere
      this.canvas._finalizeCurrentTransform(eventData.e);
      this.canvas._currentTransform = null;
    }

    this.shapeControl?.drawingComplete();
    this.drawingComplete?.();
    this.canvas.controller.actionShowObjectToolbar();
    this.canvas.controller.undoManager.completeUndoGroup();

    return false;
  }

  buildPreviewShape() {
    if (!this.originalObject || !this.shapeControl) {
      throw new Error(
        'Must provide originalObject and shapeControl in order to use buildPreviewShape'
      );
    }

    return buildShape(
      this.originalObject,
      this.shapeControl.attachmentPoint,
      this.shapeControl.normal
    );
  }

  private _createConnector({
    eventData,
    drawing,
    clickStart,
  }: {
    eventData: MouseEventData;
    drawing: boolean;
    clickStart: ClickStart;
  }) {
    const canvas = this.canvas;
    const startPoint = clickStart.pointer;
    let endPoint;
    if (drawing) {
      endPoint = { x: startPoint.x + 0.1, y: startPoint.y + 0.1 };
    } else {
      endPoint = { x: startPoint.x + DEFAULT_LENGTH, y: startPoint.y };
    }

    const { iconName, ...options } = this.initialOptions ?? {};

    const controller = this.canvas.controller;
    this.connector = Connector.create([startPoint, endPoint], {
      controller,
      ...controller.defaultsManager.for('connector'),
      ...options,
      drawing,
    });
    this.connector.beingControlled = true;

    canvas.setActiveObject(this.connector);
    canvas.controller.actionHideObjectToolbar();
    canvas.isDrawingMode = false;

    if (this.shapeControl) {
      // Attach the start of the connector to the shape.
      this.shapeControl.attachStart(this.connector);
    }

    if (drawing) {
      // Stop free drawing and setup the canvas transform information as if
      // the user had started dragging on our new connector.
      canvas.isDrawingMode = false;

      canvas._isCurrentlyDrawing = false;

      this._setupTransform(eventData.e, this.connector);
    }

    if (iconName) {
      this.connector.createIconLabel({
        iconName,
      });
    }

    return this.connector;
  }

  _setupTransform(e: Event, connector: Connector) {
    connector.__corner = 'p1';
    this.canvas._setupCurrentTransform(e, connector, true);
  }

  private handleClick(shapeControl: ShapeConnectControl) {
    const sourceObj = this.originalObject;

    if (!sourceObj) {
      console.error('Must create connector from existing shape');
      return;
    }

    // Placeholder object to select after creating the connector.
    let obj: fabric.Object;

    // Check if we should connect to nearby shape
    if (shapeControl.nearbyShape && shapeControl.targetAttachmentPoint) {
      obj = shapeControl.nearbyShape;
      this.createConnectorBetweenShapes(
        sourceObj,
        shapeControl.nearbyShape,
        shapeControl.attachmentPoint,
        shapeControl.targetAttachmentPoint
      );
    } else {
      obj = buildShapeAndConnector(
        sourceObj,
        shapeControl.attachmentPoint,
        shapeControl.oppositeAttachmentPoint ?? AttachmentPoint.Center,
        shapeControl.normal
      );
    }

    // Select the shape rather than the connector.
    this.canvas.setActiveObject(obj);
  }

  private createConnectorBetweenShapes(
    sourceShape: Attachable,
    targetShape: Attachable,
    sourceAttachment: AttachmentPoint,
    targetAttachment: AttachmentPoint
  ): Connector {
    // For this particular use case, prefer center attachment points for both shapes.
    // The center point gives us auto-adjustment behavior, which we prioritize
    // for the target shape. Having both shapes use center if possible feels a
    // bit more consistent and satisfying.
    if (sourceShape.attachmentPoints().includes(AttachmentPoint.Center)) {
      sourceAttachment = AttachmentPoint.Center;
    }

    // Get attachment positions using indicator method since connector doesn't exist yet
    const startPoint = calculateAttachmentPosition(
      sourceShape,
      sourceAttachment,
      { withStandoff: true }
    );

    const endPoint = calculateAttachmentPosition(
      targetShape,
      targetAttachment,
      { withStandoff: true }
    );

    if (!startPoint || !endPoint) {
      throw new Error('Could not calculate attachment positions');
    }

    // Create connector
    const controller = this.canvas.controller;
    const connector = Connector.create([startPoint, endPoint], {
      controller,
      ...controller.defaultsManager.for('connector'),
    });

    // Attach to both shapes
    sourceShape.attachConnector(
      connector,
      ConnectorEnd.CONNECTOR_START,
      sourceAttachment
    );
    targetShape.attachConnector(
      connector,
      ConnectorEnd.CONNECTOR_END,
      targetAttachment
    );

    // Add to canvas
    this.canvas.add(connector);

    connector.constrainConnector({});

    return connector;
  }

  _render() {}
}

export default ConnectorDrawingBrush;
